import { ObjectHash } from '../../types/shared'
import { Theme } from '../../types/shared'
import ChartEvent from '../chart-events/chart-event'
import { ChartEventBadgeType } from '../constants/common'
import { chartEventsAsyncModule } from '../controllers/chart-events/async-modules'
import ElementModel from '../models/element'
import { ChartEventBadgeState, IChartEventBadgeProps } from '../utils/chart-events-utils'
import { importWithRetry } from '../utils/import-with-retry'
import { CHART_EVENT_BADGE_SIZE } from './constants'
import Pane from './pane'

const badgeImages = {} as ObjectHash<HTMLImageElement, string>

type ChartEventBadgeCommonType = Omit<IChartEventBadgeProps, 'width'> & { badgeType: ChartEventBadgeType }

function getSvgImageCacheKey(props: Pick<ChartEventBadgeCommonType, 'badgeType' | 'theme' | 'state'>) {
  return `${props.badgeType}-${props.theme}-${props.state}`
}

async function fetchAndCacheChartEventBadges({ onBadgeInitLoad }: { onBadgeInitLoad: () => void }) {
  // Currently node charts don't render badges and if the code below is executed on server it will crash because of missing file which is not transpiled
  if (typeof window === 'undefined') {
    return
  }

  const { chartBadgeSvgByType } = await importWithRetry(chartEventsAsyncModule.importFn)

  Object.values(ChartEventBadgeState).forEach((badgeState) => {
    Object.values(ChartEventBadgeType).forEach((badgeType) => {
      Object.values(Theme).forEach((theme) => {
        const cacheKey = getSvgImageCacheKey({
          badgeType,
          state: badgeState,
          theme,
        })
        const svgHTML = chartBadgeSvgByType[badgeType]({
          state: badgeState,
          theme,
          width: CHART_EVENT_BADGE_SIZE,
        })
        const img = new Image()
        img.src = `data:image/svg+xml,${encodeURIComponent(svgHTML)}`
        img.onload = () => {
          onBadgeInitLoad()
        }
        badgeImages[cacheKey] = img
      })
    })
  })
}

class ChartEventElement extends ElementModel {
  static initClass(paneModel: typeof Pane) {
    this.configure('ChartEventElement', 'instance', 'lastChange', 'zIndex', 'yIndex', 'elementId')
    this.belongsTo('pane', paneModel)
  }

  declare instance: ChartEvent
  yIndex = 0

  getChartEventBadgeImage(props: ChartEventBadgeCommonType) {
    const cacheKey = getSvgImageCacheKey(props)
    if (badgeImages[cacheKey]) {
      return badgeImages[cacheKey]
    }

    void fetchAndCacheChartEventBadges({
      onBadgeInitLoad: () => this.trigger('change', this),
    })
  }

  getChartEventData() {
    return this.instance.model
      .chart()
      .quote()
      ?.chartEvents?.find((stockEvent) => stockEvent.elementId === this?.elementId)
  }

  toObject() {
    return {
      ...super.toObject(),
      yIndex: this.yIndex,
    }
  }

  toConfig() {
    return {
      ...super.toConfig(),
      yIndex: this.yIndex,
    }
  }
}

export default ChartEventElement
